import { Button, Flex, Modal } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { EditFilled, EyeFilled } from '@ant-design/icons';
import { CustomButton, CustomParagraph, CustomTitle } from '../../components';
import emailAvatar from '../../assets/images/emailAvatar.svg';
import useAi from '../../assets/images/useAi.svg';
import { useToken } from '../../common/helper';
import { generateEmailParts } from '../../common/emailTemplates';
import { ShipmentAlertData, SingleShipmentDataExtended } from '@/apis/types';
import styles from './EmailTranscriptModal.module.css';

interface EmailTranscriptModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (message: string, subject: string) => void;
  shipment?: SingleShipmentDataExtended;
  alert?: ShipmentAlertData;
}

/**
 * Modal for composing and editing AI-generated email messages for shipment alerts
 *
 * @param open - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param onConfirm - Callback when email is confirmed with message and subject
 * @param shipment - Shipment data for email context
 * @param alert - Alert data for email generation
 */
const EmailTranscriptModal: React.FC<EmailTranscriptModalProps> = ({
  open,
  onClose,
  onConfirm,
  shipment,
  alert,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { decodedUserToken } = useToken();
  const [message, setMessage] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [subject, setSubject] = useState('');
  const editableRef = useRef<HTMLDivElement>(null);
  const [emailFooter, setEmailFooter] = useState('');

  useEffect(() => {
    if (alert && shipment) {
      const {
        subject: newSubject,
        body: newBody,
        footer: emialFooter,
      } = generateEmailParts({
        alert,
        shipment,
        decodedToken: decodedUserToken() ?? undefined,
      });
      setEmailFooter(emialFooter);

      let cleanedBody = newBody || '';
      const lowerBody = cleanedBody.toLowerCase();

      const isOrigin = /\bcontact\s+(the\s+)?origin(\s+contact)?\b/i.test(
        lowerBody
      );
      const isDestination =
        /\bcontact\s+(the\s+)?destination(\s+contact)?\b/i.test(lowerBody);
      const isTransportProvider =
        /\bcontact\s+(the\s+)?transport\s+provider\b/i.test(lowerBody);

      if (isOrigin || isDestination) {
        cleanedBody = cleanedBody.replace(
          /\b(please\s+)?contact\s+(the\s+)?(origin|destination)(\s+contact)?\s+to\s*/gi,
          'Please contact the transport provider to '
        );
      } else if (isTransportProvider) {
        cleanedBody = cleanedBody.replace(
          /\b(please\s+)?contact\s+(the\s+)?transport\s+provider\s+to\s*/gi,
          'Please '
        );
      }
      cleanedBody = cleanedBody.replace(
        /^(?!please\s)(contact\s)/i,
        'Please contact '
      );
      setSubject(newSubject);
      setMessage(cleanedBody);
      const content = cleanedBody + '\n\n' + emialFooter;
      setEmailContent(content);
    }
  }, [alert, shipment, decodedUserToken]);

  const handleToggleEdit = () => {
    if (isEditing && editableRef.current) {
      const text = editableRef.current.innerText + '\n\n' + emailFooter;
      setEmailContent(text);
      setMessage(editableRef.current.innerText);
    }
    setIsEditing((prev) => !prev);
  };

  return (
    <>
      <Modal open={open} onCancel={onClose} footer={null} centered width={600}>
        <Flex align='center' gap='8px' style={{ marginBottom: '24px' }}>
          <img
            src={useAi}
            alt='email icon'
            width={20}
            height={20}
            className={styles.titleIcon}
          />
          <CustomTitle
            text='Email using AI Assistant'
            fontSize='20px'
            lineHeight='28px'
            fontWeight='700'
            marginBottom='0px'
          />
        </Flex>

        <Flex
          justify='space-between'
          align='center'
          style={{ marginBottom: '12px' }}
        >
          <CustomParagraph
            text={isEditing ? 'Edit core message' : 'Email preview'}
            fontSize='14px'
            color='black'
            fontWeight='600'
            marginBottom='0px'
          />
          <Button
            type='text'
            onClick={handleToggleEdit}
            icon={isEditing ? <EyeFilled /> : <EditFilled />}
            style={{
              fontWeight: 600,
              fontSize: '12px',
              background: 'var(--light-gray)',
              borderRadius: '28px',
              padding: '4px 10px',
              height: '32px',
            }}
          >
            {isEditing ? 'Preview' : 'Edit message'}
          </Button>
        </Flex>

        {/* Message box */}
        <div
          className={`${styles.messageBox} ${isEditing ? styles.messageBoxEditing : styles.messageBoxPreview}`}
        >
          <Flex gap='12px' align='start'>
            {!isEditing && (
              <img src={emailAvatar} alt='avatar' width={32} height={32} />
            )}
            <div className={styles.content}>
              {!isEditing && (
                <>
                  <p className={styles.sender}>
                    {decodedUserToken()?.company_name}
                    <span className={styles.senderEmail}>
                      {`<customer@zoomlogi.com>`}
                    </span>
                  </p>

                  <p className={styles.recipient}>
                    to {alert?.primary_contact?.email}
                  </p>
                </>
              )}
              {isEditing ? (
                <div
                  ref={editableRef}
                  contentEditable
                  suppressContentEditableWarning
                  className={styles.editable}
                >
                  {message}
                </div>
              ) : (
                <p className={styles.body}>{emailContent}</p>
              )}
            </div>
          </Flex>
        </div>
        <p className={`${styles.note} mb-2`}>
          <span className={styles.noteLabel}>Note:</span>{' '}
          {`Once the initial email
          is sent, the ${decodedUserToken()?.company_name}'s AI assistant will
          handle the communication with the recipient thereafter. You will be CC'd and you can jump in the conversation to stop the AI assistant at any time.
          `}
        </p>

        <Flex justify='end' gap='16px'>
          <CustomButton
            text='Cancel'
            onClick={onClose}
            bgColor='transparent'
            color='black'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
          />
          <CustomButton
            text='Start AI email thread'
            onClick={() => {
              const finalMessage = emailContent;
              onConfirm(finalMessage, subject); //start ai email thread
            }}
            bgColor='black'
            color='white'
            fontSize='14px'
            fontWeight='600'
            height='48px'
            borderRadius='8px'
          />
        </Flex>
      </Modal>
    </>
  );
};

export default EmailTranscriptModal;
